// The player's own ship, the thrusters it burns, and the lateral push the
// flight assist commands off them each tick.
import * as THREE from 'three';
import { createCustos, type Custos, type ModuleHit } from '../scene/custos';
import { createThrusters, thrusterLayoutFor, type ThrusterDrive, type Thrusters } from '../scene/thrusters';
import { parsePlumeLook } from '../scene/drivePlume';
import { AUTHORED_SPECS, SHIP_SPECS, type ShipSpec } from '../scene/shipSpec';
import { DEFAULT_PLAYER_SHIP } from '../state/gameState';
import { flightNose, type FlightConfig, type FlightState, type FlightVec } from '../sim/flight';
import { TEST_SWITCHES } from '../urlFlags.ts';

// `?thrustersoff=1`, `?plumefixed=1`, `?lateralalways=1` and `?plumeweak=1`
// are the broken-on-purpose variants `tools/preview/thruster-check.mjs` opens
// with `--inject thrusters-off`, `--inject plume-fixed`,
// `--inject lateral-always` and `--inject plume-weak`. Nothing a player
// reaches sets any of them.
const THRUSTERS_OFF = TEST_SWITCHES && new URLSearchParams(location.search).get('thrustersoff') === '1';
const PLUME_FIXED = TEST_SWITCHES && new URLSearchParams(location.search).get('plumefixed') === '1';
const LATERAL_ALWAYS = TEST_SWITCHES && new URLSearchParams(location.search).get('lateralalways') === '1';
const PLUME_WEAK = TEST_SWITCHES && new URLSearchParams(location.search).get('plumeweak') === '1';
// `?plume=subtle|cinematic|vivid` picks the drive plumes' variant (production
// is always cinematic); `?plumehard=1` and `?plumetint=magenta` are the two
// controls `tools/preview/plume-check.mjs` must fail.
const PLUME_LOOK = parsePlumeLook(TEST_SWITCHES ? new URLSearchParams(location.search).get('plume') : null);
const PLUME_HARD = TEST_SWITCHES && new URLSearchParams(location.search).get('plumehard') === '1';
const PLUME_MAGENTA = TEST_SWITCHES && new URLSearchParams(location.search).get('plumetint') === 'magenta';
/** `?swapleak=1`: a hull swap leaves the old hull and its thrusters
 *  undisposed, the control `tools/preview/ship-picker-check.mjs --inject leak`
 *  must fail. Nothing a player reaches sets it. */
const SWAP_LEAK = TEST_SWITCHES && new URLSearchParams(location.search).get('swapleak') === '1';

/** Play's opening hull, a SHIP_SPECS key: HOMV Custos (PF-1), the Custos-class
 *  Patrol Frigate, the authored "Tower" hull. The hull flown is the state's
 *  own (`player.shipSpec`, `src/state/gameState.ts`), which opens on this and
 *  which the Ship Yard's picker moves; `src/main.ts` keys the player's SSD off
 *  it. Explore does not: it flies the Pup, whose hull and radiator GLBs are on
 *  its allowlist (EXPLORE_HULL_PARTS in vite.config.ts). */
export const PLAYER_SHIP_SPEC = DEFAULT_PLAYER_SHIP;

/** What the picker offers, in its order: the four authored hulls, then the
 *  procedural Custos PF and the Iuventius Pup, which is an authored hull
 *  (`spec.hull`) and one Ship Yard choice. */
export const PLAYER_SHIP_CHOICES: readonly string[] = [
  ...Object.keys(AUTHORED_SPECS), 'pf', 'pup',
].filter((k) => k in SHIP_SPECS);
/** The frigate is 14.8 units long in its builder's own units (~10 m each, the
 *  procedural Custos scale). 0.45 - the scale the Pup flew at - draws it 6.7
 *  long: about the Cutter and Sich escorts it fights beside
 *  (src/game/shipSeeds.ts), and it puts the bow at 3.3, just inside the
 *  3.0 to 3.4 muzzle offsets the weapons fire from. */
const PLAYER_SHIP_SCALE = 0.45;

export interface PlayerShipDeps {
  /** SHIP_SPECS key of the hull to fly. */
  variant: string;
  camera: THREE.PerspectiveCamera;
  bubble: THREE.Group;
  flight: FlightState;
  flightConfig: FlightConfig;
  accelMps2: number;
  /** `?assistpushnose=1`: whether the lateral push is solved off the nose
   *  rather than off the assist's own recorded turn plane. */
  assistPushNose: boolean;
}

export interface PlayerShipHandle {
  /** The hull the player flies, behind a face that outlives a swap: `group` is
   *  the same object for the whole session and every other member reads the
   *  hull drawn now, so a caller holding `ship` never holds a disposed one. */
  ship: Custos;
  /** The drawn hull's thrusters, behind the same kind of face. */
  thrusters: Thrusters;
  /** SHIP_SPECS key of the hull drawn now. */
  readonly variant: string;
  /** Scene metres from the ship's origin to the farthest point of the drawn
   *  hull, drive plumes left out: the sphere the chase camera stays outside. */
  hullRadius(): number;
  /**
   * Fly another hull: the drawn one and its thrusters are disposed and the
   * picked one built in their place, with its own thruster layout, plumes,
   * radiator glow and running lights. The heat stored, the radiator posture
   * and the drive level carry across; module damage does not, since the two
   * hulls do not share module ids. The flight model is untouched.
   */
  swap(variant: string): void;
  /** The lateral push this tick, in the ship's own axes, and the delta-v it
   *  cost; writes the result into `thrustPush`. See the function body for
   *  the model. */
  thrustLateral(dvTick: number, throttle: number, dt: number): number;
  /** Rebuilt per tick by `main.ts` and `cruise/drive.ts`; nothing here
   *  allocates once the scene is up. */
  thrustDrive: ThrusterDrive;
  thrustPush: THREE.Vector3;
}

export function createPlayerShip(deps: PlayerShipDeps): PlayerShipHandle {
  const { camera, bubble, flight, flightConfig, accelMps2, assistPushNose } = deps;

  // ---------- Player ship ----------
  // Play: HOMV Custos (PF-1), the Custos-class Patrol Frigate, opening the game
  // as a heavy combat vehicle, or whichever hull the player has picked since.
  // Explore: the Iuventius Pup. Radiators out - both open at cruise.
  //
  // The ship and the camera ride INSIDE the bubble: the bubble is the ship's own
  // orbital frame, so the 2D integrator, the chase camera and every entity in the
  // fight stay in one set of local metres and none of them knows the frame is
  // tilted onto Mars's orbital plane on one run and Pluto's on the next. The
  // group the flight moves is this module's own and outlives a hull swap; the
  // hull drawn now hangs inside it at identity.
  const group = new THREE.Group();
  group.name = 'player-ship';
  bubble.add(group);
  bubble.add(camera);

  let variant = '';
  let cur: Custos | null = null;
  let curThrusters: Thrusters | null = null;
  let curSpec: ShipSpec | null = null;
  let damageCb: ((hit: ModuleHit) => void) | null = null;
  let radius = 0;
  let radiusAtMs = 0;

  function build(next: string): void {
    const spec = SHIP_SPECS[next];
    if (!spec) throw new Error(`createPlayerShip: unknown ship spec "${next}"`);
    const was = cur;
    const built = createCustos({ variant: next, scale: PLAYER_SHIP_SCALE, initialDeploy: 1 });
    built.group.name = 'player-hull';
    if (was) {
      Object.assign(built.heat, { ...was.heat, panelsPerSide: built.heat.panelsPerSide });
      built.restoreDeploy(was.state.deployFraction, was.state.deployTarget);
      built.setThrottle(was.state.throttle);
    }
    if (damageCb) built.damage.onChange(damageCb);
    // What the stern plume, the retro nozzles and the lateral thrusters burn is
    // driven off the flight step's own numbers rather than off the keys: the
    // flight assist fires lateral thrusters with nobody's hands on the controls,
    // so the signal for that half is the delta-v the tick actually spent.
    const builtThrusters = createThrusters({
      camera,
      hull: built.visual,
      setMainBurn: (level: number) => built.setThrottle(level),
      accelMps2,
      suppressed: THRUSTERS_OFF,
      fixedPlume: PLUME_FIXED,
      lateralAlways: LATERAL_ALWAYS,
      weakPlume: PLUME_WEAK,
      layout: thrusterLayoutFor(spec),
      spec,
      look: PLUME_LOOK,
      hardPlume: PLUME_HARD,
      magentaPlume: PLUME_MAGENTA,
    });
    if (was) {
      if (!SWAP_LEAK) {
        curThrusters?.dispose();
        was.dispose();
      }
      group.remove(was.group);
    }
    group.add(built.group);
    cur = built;
    curThrusters = builtThrusters;
    curSpec = spec;
    variant = next;
    radius = 0;
    radiusAtMs = 0;
  }
  build(deps.variant);

  const live = (): Custos => cur!;
  const ship: Custos = {
    group,
    get visual() { return live().visual; },
    setDeployTarget: (f) => live().setDeployTarget(f),
    restoreDeploy: (fraction, target) => live().restoreDeploy(fraction, target),
    toggleDeploy: () => live().toggleDeploy(),
    setThrottle: (t) => live().setThrottle(t),
    get state() { return live().state; },
    get heat() { return live().heat; },
    get radiator() { return live().radiator; },
    update: (dt, t) => live().update(dt, t),
    dispose: () => {
      curThrusters?.dispose();
      live().dispose();
      group.removeFromParent();
    },
    damage: {
      states: () => live().damage.states(),
      damageAt: (worldHit) => live().damage.damageAt(worldHit),
      setState: (id, state) => live().damage.setState(id, state),
      reset: () => live().damage.reset(),
      restoreStates: (states) => live().damage.restoreStates(states),
      driveFactor: () => live().damage.driveFactor(),
      onChange: (cb) => { damageCb = cb; live().damage.onChange(cb); },
    },
  };
  const thrusters: Thrusters = {
    get group() { return curThrusters!.group; },
    update: (drive) => curThrusters!.update(drive),
    read: () => curThrusters!.read(),
    dispose: () => curThrusters!.dispose(),
  };

  /**
   * An authored hull's radius comes off its measured profile, which is the
   * shipped mesh's own envelope and is there before the GLB lands. A procedural
   * one is measured off what is drawn, again each second while its parts are
   * still arriving, keeping the largest.
   */
  function hullRadius(): number {
    const man = curSpec!.hull?.manifest;
    if (man) {
      if (radius > 0) return radius;
      const p = man.profile;
      let r2 = 0;
      for (let i = 0; i < p.r.length; i++) {
        const z = Math.max(Math.abs(p.z0 + i * p.pitch), Math.abs(p.z0 + (i + 1) * p.pitch));
        r2 = Math.max(r2, (p.r[i] ?? 0) ** 2 + z * z);
      }
      radius = Math.sqrt(r2) * PLAYER_SHIP_SCALE;
      return radius;
    }
    const now = performance.now();
    if (radius > 0 && now - radiusAtMs < 1000) return radius;
    radiusAtMs = now;
    radius = Math.max(radius, measuredRadius(live().group));
    return radius;
  }

  /** Rebuilt per tick; nothing here allocates once the scene is up. */
  const thrustDrive: ThrusterDrive = {
    cruise: false, throttle: 0, lateralDvMps: 0, realDt: 0, pushBody: { x: 0, y: 0, z: 0 },
  };
  const _thrustNose: FlightVec = { x: 0, y: 0, z: 0 };
  const thrustPush = new THREE.Vector3();
  const _thrustQuat = new THREE.Quaternion();

  /**
   * The lateral push this tick, in the ship's own axes, and the delta-v it cost.
   *
   * `stepFlight` charges only two things to `deltaVSpent`: the main burn, which
   * is exactly `|accel * throttle * dt|` over the whole step, and the assist. So
   * what is left after the first is the second, with no hook inside the flight
   * model and nothing read off the keyboard.
   *
   * The direction is the one the assist itself turned in: `state.assistPush`,
   * every substep's own turn plane weighted by the arc it turned. It is recorded
   * there rather than solved back out here because the turn ends ON the aim, so a
   * step that closed the gap leaves nothing in the after-state to read - and
   * solving it from the NOSE, which is what this did, stopped being right the
   * moment the aim began riding gravity's own turning of the velocity while the
   * nose stood still in the frame. The two separate at `g / speed` radians a
   * simulated second, which at a moon's orbit is a degree a minute of sim and a
   * degree every twentieth of a second of wall at a clock rate of 1200.
   */
  function thrustLateral(dvTick: number, throttle: number, dt: number): number {
    const burned = Math.abs(flightConfig.accel * throttle * dt);
    const lateral = dvTick - burned;
    // A floor, not zero: subtracting one large burn from another leaves float
    // dust that would otherwise read as a lateral push with a direction.
    if (!(lateral > 1e-9)) {
      thrustPush.set(0, 0, 0);
      return 0;
    }
    const push = flight.assistPush;
    if (assistPushNose) {
      const v = flight.velocity;
      const speed = Math.hypot(v.x, v.y, v.z);
      const nose = flightNose(flight, _thrustNose);
      if (!(speed > 0)) {
        thrustPush.set(0, 0, 0);
        return lateral;
      }
      const cos = (v.x * nose.x + v.y * nose.y + v.z * nose.z) / speed;
      thrustPush.set(
        nose.x - (cos * v.x) / speed,
        nose.y - (cos * v.y) / speed,
        nose.z - (cos * v.z) / speed,
      );
    } else thrustPush.set(push.x, push.y, push.z);
    if (!(thrustPush.lengthSq() > 0)) {
      thrustPush.set(0, 0, 0);
      return lateral;
    }
    thrustPush.normalize().applyQuaternion(
      _thrustQuat.set(flight.orientation.x, flight.orientation.y, flight.orientation.z,
        flight.orientation.w).invert(),
    );
    return lateral;
  }

  return {
    ship, thrusters, thrustLateral, thrustDrive, thrustPush, hullRadius,
    get variant(): string { return variant; },
    swap(next: string): void {
      if (next !== variant) build(next);
    },
  };
}

/** The name `src/scene/thrusters.ts` gives the group it mounts on a hull. */
const THRUSTERS_GROUP_NAME = 'thrusters';
const _mRoot = new THREE.Matrix4();
const _mRel = new THREE.Matrix4();
const _mCorner = new THREE.Vector3();

/** Farthest corner of any drawn mesh's own box from `root`'s origin, in
 *  `root`'s frame. The thrusters' subtree and the hull's own plumes are left
 *  out: a flame is not hull, and a full burn's is several ship-lengths long. */
function measuredRadius(root: THREE.Object3D): number {
  root.updateMatrixWorld(true);
  _mRoot.copy(root.matrixWorld).invert();
  let r2 = 0;
  const visit = (o: THREE.Object3D): void => {
    if (o.name === THRUSTERS_GROUP_NAME || /plume/i.test(o.name)) return;
    const mesh = o as THREE.Mesh;
    const box = mesh.isMesh ? (mesh.geometry.boundingBox ?? (mesh.geometry.computeBoundingBox(), mesh.geometry.boundingBox)) : null;
    if (box) {
      _mRel.multiplyMatrices(_mRoot, mesh.matrixWorld);
      for (let i = 0; i < 8; i++) {
        _mCorner.set(i & 1 ? box.max.x : box.min.x, i & 2 ? box.max.y : box.min.y,
          i & 4 ? box.max.z : box.min.z).applyMatrix4(_mRel);
        r2 = Math.max(r2, _mCorner.lengthSq());
      }
    }
    for (const c of o.children) visit(c);
  };
  visit(root);
  return Math.sqrt(r2);
}
